import React, { useState, useEffect } from 'react';
import api from './api';
import ConfirmDeleteModal from './components/ConfirmDeleteModal';
import ProductList from './components/ProductList';
import ProductForm from './components/ProductForm';
import BulkOperations from './components/BulkOperations';
import SearchFilters, { SearchFiltersType } from './components/SearchFilters';
import CategoryManager from './components/CategoryManager';
import TaxonomyManager from './components/TaxonomyManager';
import AttributeSetup from './components/AttributeSetup';
import InventoryDashboard from './components/InventoryDashboard';
import GoodsReceiptManager from './components/GoodsReceiptManager';
import InventoryStockList from './components/InventoryStockList';
import StockMovementList from './components/StockMovementList';
import StockAdjustmentManager from './components/StockAdjustmentManager';
import StockTransferManager from './components/StockTransferManager';
import ReservationManager from './components/ReservationManager';
import JobOrderManager from './components/JobOrderManager';
import ClientCatalog from './components/ClientCatalog';
import AdminSettings from './components/AdminSettings';
import WarehouseManager from './components/WarehouseManager';
import ImageManagementDashboard from './components/ImageManagementDashboard';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import ManagerCommandCenter from './components/ManagerCommandCenter';
import ReportsModule from './components/ReportsModule';
import LoginForm from './components/LoginForm';
import UserProfile from './components/UserProfile';
import OrderList from './components/OrderList';
import OrderForm from './components/OrderForm';
import OrderDetail from './components/OrderDetail';
import OrderImport from './components/OrderImport';
import OrderDashboard from './components/OrderDashboard';
import NotificationCenter from './components/NotificationCenter';
import AuditLogs from './components/AuditLogs';
import UserManagement from './components/UserManagement';
import LogisticsDashboard from './components/LogisticsDashboard';
import NDRManagement from './components/NDRManagement';
import RTOManagement from './components/RTOManagement';
import CarrierManager from './components/CarrierManager';
import RateCardManager from './components/RateCardManager';
import VendorManager from './components/VendorManager';
import BundleComponentManager from './components/BundleComponentManager';
import ShipmentBookingForm from './components/ShipmentBookingForm';
import ShipmentTracking from './components/ShipmentTracking';
import ErrorBoundary from './components/ErrorBoundary';
import ChannelManager from './components/ChannelManager';
import PackageManager from './components/PackageManager';
import PurchaseOrderManager from './components/PurchaseOrderManager';

export interface Product {
  id: number;
  sku: string;
  name: string;
  description?: string;
  price?: number;
  brand?: string;
  product_type?: string;
  status: string;
  workflow_status?: string;
  inventory_available?: number;
  category_id?: number;
  department_id?: number;
  subcategory_id?: number;
  is_variant?: boolean;
  parent_id?: number;
  category?: {
    id: number;
    name: string;
  };
  department?: {
    id: number;
    name: string;
  };
  subcategory?: {
    id: number;
    name: string;
  };
  attributes?: Array<{
    attribute_id: number;
    attribute_name: string;
    value: string;
  }>;
  images?: Array<{
    id: number;
    image_url: string;
    image_type: string;
    alt_text?: string;
    sort_order: number;
    is_primary: boolean;
  }>;
  variants?: Product[];
}

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

const App: React.FC = () => {
  // Authentication state
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Sync failure banner state
  const [syncFailures, setSyncFailures] = useState<Array<{channel_name: string; type: string; message: string; failed_at?: string}>>([]);
  const [syncBannerDismissed, setSyncBannerDismissed] = useState(false);

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});

  // Navigation state
  const [activeView, setActiveView] = useState('orders');
  const [productSubView, setProductSubView] = useState('catalog');
  const [inventoryView, setInventoryView] = useState('stock');
  const [orderView, setOrderView] = useState('list');

  // Tab system (max 5 tabs)
  interface AppTab {
    id: string;
    view: string;
    label: string;
    entityId?: number;
    entityType?: string; // 'order-detail', 'product-detail', etc.
  }
  const [tabs, setTabs] = useState<AppTab[]>([{ id: 'tab-1', view: 'orders', label: 'Orders' }]);
  const [activeTabId, setActiveTabId] = useState('tab-1');
  let tabCounter = React.useRef(1);

  const TAB_LABELS: Record<string, string> = {
    'command-center': 'Command Center',
    products: 'Products', inventory: 'Inventory',
    orders: 'Orders', logistics: 'Logistics', purchaseOrders: 'Purchase Orders', reports: 'Reports', analytics: 'Analytics', audit: 'Audit Logs',
    settings: 'Settings', carriers: 'Settings', rateCards: 'Settings', vendors: 'Settings', channels: 'Settings', packages: 'Settings', admin: 'Settings',
  };

  const openTab = (view: string, label?: string, entityId?: number, entityType?: string) => {
    // If same view already open (without entity), just activate it
    if (!entityId) {
      const existing = tabs.find(t => t.view === view && !t.entityId);
      if (existing) {
        setActiveTabId(existing.id);
        setActiveView(view);
        return;
      }
    }
    // If entity detail already open, activate it
    if (entityId && entityType) {
      const existing = tabs.find(t => t.entityType === entityType && t.entityId === entityId);
      if (existing) {
        setActiveTabId(existing.id);
        setActiveView(existing.view);
        return;
      }
    }
    // Max 10 tabs
    if (tabs.length >= 10) {
      alert('Maximum 10 tabs allowed. Please close a tab first.');
      return;
    }
    tabCounter.current += 1;
    const newTab: AppTab = {
      id: `tab-${tabCounter.current}`,
      view,
      label: label || TAB_LABELS[view] || view,
      entityId,
      entityType,
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
    setActiveView(view);
    // Set entity-specific state
    if (entityType === 'order-detail' && entityId) {
      setSelectedOrderId(entityId);
      setOrderView('detail');
    }
  };

  const closeTab = (tabId: string) => {
    const idx = tabs.findIndex(t => t.id === tabId);
    const newTabs = tabs.filter(t => t.id !== tabId);
    if (newTabs.length === 0) {
      // Always keep at least one tab
      const fallback: AppTab = { id: 'tab-fallback', view: 'analytics', label: 'Analytics' };
      setTabs([fallback]);
      setActiveTabId(fallback.id);
      setActiveView('analytics');
      return;
    }
    setTabs(newTabs);
    if (activeTabId === tabId) {
      // Switch to adjacent tab
      const nextIdx = Math.min(idx, newTabs.length - 1);
      setActiveTabId(newTabs[nextIdx].id);
      setActiveView(newTabs[nextIdx].view);
    }
  };

  const activateTab = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      setActiveTabId(tabId);
      setActiveView(tab.view);
      if (tab.entityType === 'order-detail' && tab.entityId) {
        setSelectedOrderId(tab.entityId);
        setOrderView('detail');
      } else if (tab.view === 'orders') {
        setOrderView('list');
        setSelectedOrderId(null);
      }
    }
  };

  const getActiveTab = () => tabs.find(t => t.id === activeTabId);

  // Global search state
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);
  
  // Product state
  const [products, setProducts] = useState<Product[]>([]);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalProducts, setTotalProducts] = useState(0);
  const [filters, setFilters] = useState<SearchFiltersType>({
    field: 'name',
    operator: 'contains',
    value: '',
    sortBy: 'name',
    sortOrder: 'asc',
    categoryId: null
  });
  const [categories, setCategories] = useState<Array<{id: number; name: string}>>([]);
  const [attributeCount, setAttributeCount] = useState(0);
  const [channelCount, setChannelCount] = useState(0);
  const [categoryCount, setCategoryCount] = useState(0);
  
  // Inventory state
  const [inventoryStockFilter, setInventoryStockFilter] = useState<'all' | 'low' | 'out'>('all');
  
  // Order state
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showOrderImport, setShowOrderImport] = useState(false);
  const [orderDashboardKey, setOrderDashboardKey] = useState(0);

  // Logistics state
  const [showShipmentBookingForm, setShowShipmentBookingForm] = useState(false);
  const [selectedShipmentAwb, setSelectedShipmentAwb] = useState<string | null>(null);
  const [showShipmentTracking, setShowShipmentTracking] = useState(false);
  const [logisticsDashboardKey, setLogisticsDashboardKey] = useState(0);
  const [logisticsSubView, setLogisticsSubView] = useState<'shipments' | 'ndr' | 'rto'>('shipments');
  const [orderStatusFilter, setOrderStatusFilter] = useState<string>('');

  // Channel filter state (set by ProductList, used in fetchProducts)
  const [channelFilter, setChannelFilter] = useState<string | undefined>(undefined);

  // Product search term (set by ProductList, used in fetchProducts)
  const [productSearchTerm, setProductSearchTerm] = useState<string>('');
  const [productTypeFilter, setProductTypeFilter] = useState<string>('');

  // Selected products for bulk export
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  // UI state
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated && activeView === 'products' && productSubView === 'catalog') {
      fetchProducts();
    }
  }, [isAuthenticated, activeView, productSubView, currentPage, pageSize, filters, channelFilter, productSearchTerm, productTypeFilter]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCategories();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Poll sync status every 5 minutes to detect failures
  useEffect(() => {
    if (!isAuthenticated) return;
    const checkSyncStatus = async () => {
      try {
        const res = await api.get('/channels/sync/status/all');
        if (res.data?.has_failures) {
          setSyncFailures(res.data.failures || []);
          setSyncBannerDismissed(false);
        } else {
          setSyncFailures([]);
        }
      } catch (e) {
        // Don't show banner for auth/network errors on this check
      }
    };
    checkSyncStatus();
    const interval = setInterval(checkSyncStatus, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setAuthLoading(false);
      return;
    }

    try {
      const response = await api.get('/users/me');
      setUser(response.data);
      setIsAuthenticated(true);
      applyDefaultLandingView(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('auth_token');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = async (username: string, password: string) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await api.post('/token', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      localStorage.setItem('auth_token', response.data.access_token);
      await checkAuth();
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  const handleUserUpdate = (updatedUser: User) => {
    setUser(updatedUser);
  };

  // On first sign-in, managers land on the Command Center by default. Everyone
  // else keeps the standard landing view (Orders) but can navigate to it.
  const applyDefaultLandingView = (u: User | null) => {
    if (u?.role === 'manager') {
      setActiveView('command-center');
      setTabs([{ id: 'tab-1', view: 'command-center', label: 'Command Center' }]);
      setActiveTabId('tab-1');
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const fetchProducts = async () => {
    try {
      const params: any = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder
      };

      if (productSearchTerm.trim()) {
        params.q = productSearchTerm.trim();
      }

      if (filters.value && filters.field && filters.operator) {
        params.search_field = filters.field;
        params.search_operator = filters.operator;
        params.search_value = filters.value;
      }

      if (filters.categoryId) {
        params.category_id = filters.categoryId;
      }

      if (channelFilter) {
        params.channel_id = channelFilter;
      }

      if (productTypeFilter) {
        params.product_type = productTypeFilter;
      }

      const response = await api.get('/products/', { params });
      setProducts(response.data.products || []);
      setTotalProducts(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories/');
      setCategories(response.data);
      setCategoryCount(response.data.length);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
    // Fetch attribute count
    try {
      const attrRes = await api.get('/api/taxonomy/subcategories');
      // Use subcategories count as a proxy, or fetch attributes directly
    } catch (e) {}
    try {
      const attrRes = await api.get('/attributes/');
      setAttributeCount(Array.isArray(attrRes.data) ? attrRes.data.length : 0);
    } catch (e) { /* attributes endpoint might not exist separately */ }
    try {
      const { channelAPI } = await import('./api');
      const chRes = await channelAPI.listChannels();
      setChannelCount(Array.isArray(chRes.data) ? chRes.data.length : 0);
    } catch (e) {}
  };

  const handleDelete = async (id: number) => {
    const product = products.find(p => p.id === id);
    setDeleteConfirm({show: true, id, name: product?.name || product?.sku || `Product #${id}`});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      const response = await api.delete(`/products/${deleteConfirm.id}`);
      console.log('Delete response:', response.status);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchProducts();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete product';
      alert(`Failed to delete product: ${errorMessage}`);
    }
  };

  const handleBulkDelete = async (ids: number[]) => {
    try {
      await Promise.all(ids.map(id => api.delete(`/products/${id}`)));
      fetchProducts();
    } catch (error) {
      console.error('Error deleting products:', error);
      alert('Failed to delete some products. Please try again.');
    }
  };

  const handleEdit = (product: Product) => {
    setViewingProduct(product);
    setShowForm(false);
  };

  const handleEditFromView = () => {
    if (viewingProduct) {
      setEditingProduct(viewingProduct);
      setViewingProduct(null);
      setShowForm(true);
    }
  };

  const handleDuplicateProduct = async (product?: Product) => {
    const source = product || viewingProduct;
    if (!source || !source.id) return;

    try {
      // Call backend duplicate endpoint (handles variant children too)
      const res = await api.post(`/products/${source.id}/duplicate`);
      const newId = res.data.id;
      const newSku = res.data.sku;
      const childrenCreated = res.data.children_created || 0;

      alert(
        `Product duplicated as ${newSku}${childrenCreated > 0 ? ` with ${childrenCreated} variant children` : ''}.\n\n` +
        `Please update the Name, Decoration, and HSN code for the new product.`
      );

      // Fetch the new product and open it in edit mode
      const productRes = await api.get(`/products/${newId}`);
      setEditingProduct(productRes.data);
      setViewingProduct(null);
      setShowForm(true);
      fetchProducts();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to duplicate product');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleFormSubmit = () => {
    fetchProducts();
    handleFormClose();
  };

  const handleOrderSelect = (orderId: number, orderNumber?: string) => {
    openTab('orders', orderNumber || `Order #${orderId}`, orderId, 'order-detail');
  };

  const handleOrderBack = () => {
    // Close the detail tab and go back to order list
    const currentTab = getActiveTab();
    if (currentTab && currentTab.entityType === 'order-detail') {
      closeTab(currentTab.id);
    } else {
      setOrderView('list');
      setSelectedOrderId(null);
    }
    setOrderDashboardKey(k => k + 1);
  };

  const handleOrderFormOpen = () => {
    setShowOrderForm(true);
    setOrderView('form');
  };

  const handleOrderFormClose = () => {
    setShowOrderForm(false);
    setOrderView('list');
    setOrderDashboardKey(k => k + 1);
  };

  const handleOrderImportOpen = () => {
    setShowOrderImport(true);
    setOrderView('import');
  };

  const handleOrderImportClose = () => {
    setShowOrderImport(false);
    setOrderView('list');
    setOrderDashboardKey(k => k + 1);
  };

  // Product export handler
  const handleProductExport = async () => {
    try {
      const params: any = {};
      if (channelFilter) {
        params.channel_id = channelFilter;
      }
      const response = await api.get('/products/export/', {
        responseType: 'blob',
        params,
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Export failed');
    }
  };

  const handleDashboardFilterClick = (filterType: string, filterValue: string) => {
    if (activeView === 'inventory') {
      setInventoryStockFilter(filterValue as 'all' | 'low' | 'out');
    } else if (activeView === 'orders') {
      setOrderStatusFilter(filterValue);
    }
  };

  const handleGlobalSearch = async (query: string) => {
    if (!query.trim()) { setGlobalSearchResults([]); setShowSearchResults(false); return; }
    setSearching(true);
    setShowSearchResults(true);
    
    const results: any[] = [];
    
    try {
      // Search products by SKU or name
      const prodRes = await api.get('/products/', { params: { q: query, limit: 5 } });
      const allProducts = prodRes.data.products || [];
      const seenIds = new Set<number>();
      for (const p of allProducts) {
        if (!seenIds.has(p.id)) {
          seenIds.add(p.id);
          results.push({ type: 'product', id: p.id, title: p.name, subtitle: p.sku, data: p });
        }
      }
    } catch (e) {}
    
    try {
      // Search orders by order number
      const orderRes = await api.get('/orders/', { params: { order_number: query, limit: 5 } });
      const orders = orderRes.data.orders || orderRes.data || [];
      for (const o of orders) {
        results.push({ type: 'order', id: o.id, title: o.order_number, subtitle: `${o.customer_name || ''} — ${o.status}`, data: o });
      }
    } catch (e) {}

    try {
      // Search job orders
      const joRes = await api.get('/inventory/job-orders/', { params: { q: query, limit: 5 } });
      const jobOrders = Array.isArray(joRes.data) ? joRes.data : [];
      for (const jo of jobOrders) {
        results.push({ type: 'job_order', id: jo.id, title: jo.job_order_number, subtitle: `${jo.decorator_name || ''} — ${jo.status}`, data: jo });
      }
    } catch (e) {}

    try {
      // Search vendors
      const vendorRes = await api.get('/vendors/', { params: { q: query, limit: 5 } });
      const vendors = Array.isArray(vendorRes.data) ? vendorRes.data : [];
      for (const v of vendors) {
        results.push({ type: 'vendor', id: v.id, title: v.name, subtitle: [v.city, v.gstin].filter(Boolean).join(' | '), data: v });
      }
    } catch (e) {}

    try {
      // Search GRNs
      const grnRes = await api.get('/inventory/goods-receipts/', { params: { q: query, limit: 5 } });
      const grns = Array.isArray(grnRes.data) ? grnRes.data : [];
      for (const g of grns) {
        results.push({ type: 'receipt', id: g.id, title: g.receipt_number, subtitle: `${g.supplier_name || ''} — ${g.status}`, data: g });
      }
    } catch (e) {}

    try {
      // Search delivery challans
      const dcRes = await api.get('/delivery-challans/', { params: { q: query, limit: 5 } });
      const dcs = Array.isArray(dcRes.data) ? dcRes.data : [];
      for (const dc of dcs) {
        results.push({ type: 'dc', id: dc.id, title: dc.dc_number, subtitle: `${dc.to_name || ''} — ${dc.dc_type}`, data: dc });
      }
    } catch (e) {}
    
    setGlobalSearchResults(results.slice(0, 15));
    setSearching(false);
  };

  if (authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm 
      onLoginSuccess={(user, token) => {
        setUser(user);
        setIsAuthenticated(true);
        applyDefaultLandingView(user);
      }}
      onError={(error) => console.error('Login error:', error)}
    />;
  }

  return (
    <div className="app-layout">
      {/* Top Header */}
      <header className="app-header">
        <div className="header-title">Logogear <span style={{ color: '#0d9488' }}>Nexus</span></div>

        {/* Global Search */}
        <div className="global-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}>
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className="global-search-input"
            placeholder="Search products, orders, SKUs..."
            value={globalSearchQuery}
            onChange={(e) => {
              setGlobalSearchQuery(e.target.value);
              handleGlobalSearch(e.target.value);
            }}
            onFocus={() => { if (globalSearchResults.length > 0) setShowSearchResults(true); }}
            onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
          />
          {!globalSearchQuery && <kbd className="global-search-shortcut">⌘K</kbd>}
          {globalSearchQuery && (
            <button onClick={() => { setGlobalSearchQuery(''); setGlobalSearchResults([]); setShowSearchResults(false); }} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '16px' }}>×</button>
          )}
          
          {/* Search Results Dropdown */}
          {showSearchResults && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100, marginTop: '6px', maxHeight: '360px', overflow: 'auto' }}>
              {searching && <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>Searching...</div>}
              {!searching && globalSearchResults.length === 0 && globalSearchQuery && (
                <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No results found</div>
              )}
              {!searching && globalSearchResults.map((result, idx) => (
                <button
                  key={`${result.type}-${result.id}-${idx}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (result.type === 'product') {
                      openTab('products');
                      setProductSubView('catalog');
                      setViewingProduct(result.data);
                    } else if (result.type === 'order') {
                      openTab('orders', `Order ${result.data?.order_number || result.id}`, result.id, 'order-detail');
                      setOrderView('detail');
                      setSelectedOrderId(result.id);
                    }
                    setGlobalSearchQuery('');
                    setGlobalSearchResults([]);
                    setShowSearchResults(false);
                  }}
                  style={{ width: '100%', padding: '10px 14px', border: 'none', borderBottom: '1px solid #f5f5f5', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px' }}
                  onMouseOver={(e) => (e.currentTarget.style.background = '#f8fafb')}
                  onMouseOut={(e) => (e.currentTarget.style.background = 'none')}
                >
                  <span style={{ fontSize: '16px', flexShrink: 0 }}>{result.type === 'product' ? '📦' : '📋'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.title}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.subtitle}</div>
                  </div>
                  <span style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, flexShrink: 0 }}>{result.type}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="header-actions">
          <button 
            className="dark-mode-toggle" 
            onClick={toggleDarkMode}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <span className="dark-mode-toggle-slider">
              {darkMode ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              )}
            </span>
          </button>
          {user && <NotificationCenter />}
          {user && <UserProfile user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />}
        </div>
      </header>

      {/* Left Sidebar Navigation */}
      <aside className={`app-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <button 
          className="sidebar-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? '▶' : '◀'}
        </button>

        <nav className="sidebar-nav">
          <button
            className={`sidebar-item ${activeView === 'command-center' ? 'active' : ''}`}
            onClick={() => openTab('command-center')}
            title="Command Center"
          >
            <span className="sidebar-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/></svg>
            </span>
            {!sidebarCollapsed && <span className="sidebar-label">Command Center</span>}
          </button>

          <button
            className={`sidebar-item ${activeView === 'products' ? 'active' : ''}`}
            onClick={() => openTab('products')}
            title="Products"
          >
            <span className="sidebar-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
            </span>
            {!sidebarCollapsed && <span className="sidebar-label">Products</span>}
          </button>

          <button
            className={`sidebar-item ${activeView === 'inventory' ? 'active' : ''}`}
            onClick={() => openTab('inventory')}
            title="Inventory"
          >
            <span className="sidebar-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h18v18H3z"/><path d="M3 9h18"/><path d="M3 15h18"/><path d="M9 3v18"/></svg>
            </span>
            {!sidebarCollapsed && <span className="sidebar-label">Inventory</span>}
          </button>

          <button
            className={`sidebar-item ${activeView === 'orders' ? 'active' : ''}`}
            onClick={() => {
              openTab('orders');
              setOrderView('list');
              setSelectedOrderId(null);
            }}
            title="Orders"
          >
            <span className="sidebar-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            </span>
            {!sidebarCollapsed && <span className="sidebar-label">Orders</span>}
          </button>

          <button
            className={`sidebar-item ${activeView === 'logistics' ? 'active' : ''}`}
            onClick={() => openTab('logistics')}
            title="Logistics"
          >
            <span className="sidebar-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
            </span>
            {!sidebarCollapsed && <span className="sidebar-label">Logistics</span>}
          </button>


          <button
            className={`sidebar-item ${activeView === 'reports' ? 'active' : ''}`}
            onClick={() => openTab('reports')}
            title="Reports"
          >
            <span className="sidebar-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            </span>
            {!sidebarCollapsed && <span className="sidebar-label">Reports</span>}
          </button>

          <button
            className={`sidebar-item ${activeView === 'analytics' ? 'active' : ''}`}
            onClick={() => openTab('analytics')}
            title="Analytics"
          >
            <span className="sidebar-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            </span>
            {!sidebarCollapsed && <span className="sidebar-label">Analytics</span>}
          </button>

          <button
            className={`sidebar-item ${activeView === 'audit' ? 'active' : ''}`}
            onClick={() => openTab('audit')}
            title="Audit Logs"
          >
            <span className="sidebar-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </span>
            {!sidebarCollapsed && <span className="sidebar-label">Audit Logs</span>}
          </button>

          {user?.role === 'admin' && (
            <button
              className={`sidebar-item ${['settings', 'carriers', 'rateCards', 'vendors', 'channels', 'packages', 'admin'].includes(activeView) ? 'active' : ''}`}
              onClick={() => openTab('carriers')}
              title="Settings"
            >
              <span className="sidebar-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </span>
              {!sidebarCollapsed && <span className="sidebar-label">Settings</span>}
            </button>
          )}
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className="app-main">
        {/* Sync Failure Banner */}
        {syncFailures.length > 0 && !syncBannerDismissed && (
          <div style={{
            background: '#fef2f2', borderBottom: '2px solid #ef4444',
            padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '12px',
            flexShrink: 0, position: 'relative', zIndex: 50,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#991b1b', marginBottom: '2px' }}>
                Channel Sync Failed
              </div>
              <div style={{ fontSize: '12px', color: '#b91c1c' }}>
                {syncFailures.map((f, i) => (
                  <span key={i}>
                    {f.channel_name} ({f.type === 'order_import' ? 'Order Import' : 'Status Push'}): {f.message}
                    {i < syncFailures.length - 1 && ' | '}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={() => setSyncBannerDismissed(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontSize: '18px', padding: '4px', lineHeight: 1 }}
              title="Dismiss"
            >×</button>
          </div>
        )}

        {/* Tab Bar */}
        {tabs.length > 0 && (
          <div style={{ display: 'flex', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', padding: '0 8px', flexShrink: 0, overflowX: 'auto', height: '36px', alignItems: 'flex-end' }}>
            {tabs.map(tab => (
              <div
                key={tab.id}
                onClick={() => activateTab(tab.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '6px 14px', fontSize: '12px', fontWeight: activeTabId === tab.id ? 600 : 400,
                  cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
                  background: activeTabId === tab.id ? 'white' : 'transparent',
                  color: activeTabId === tab.id ? '#0d9488' : '#64748b',
                  borderTopLeftRadius: '6px', borderTopRightRadius: '6px',
                  border: activeTabId === tab.id ? '1px solid #e2e8f0' : '1px solid transparent',
                  borderBottom: activeTabId === tab.id ? '1px solid white' : '1px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                <span>{tab.label}</span>
                {tabs.length > 1 && (
                  <span
                    onClick={e => { e.stopPropagation(); closeTab(tab.id); }}
                    style={{ fontSize: '14px', lineHeight: 1, color: '#94a3b8', cursor: 'pointer', marginLeft: '4px' }}
                    title="Close tab"
                  >×</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Products View */}
        {/* Manager Command Center View */}
        {(activeView === 'command-center' || tabs.some(t => t.view === 'command-center')) && (
          <div className="view-container" style={{ display: activeView === 'command-center' ? undefined : 'none' }}>
            <div className="view-content">
              <ErrorBoundary>
                <ManagerCommandCenter
                  isActive={activeView === 'command-center'}
                  onNavigate={(view) => {
                    // 'channels' lives under the admin-only Settings group; other
                    // targets are top-level views.
                    if (view === 'channels') {
                      if (user?.role === 'admin') {
                        openTab('carriers');
                        setActiveView('channels');
                      }
                    } else {
                      openTab(view);
                    }
                  }}
                />
              </ErrorBoundary>
            </div>
          </div>
        )}

        {(activeView === 'products' || tabs.some(t => t.view === 'products')) && (
          <div className="view-container" style={{ display: activeView === 'products' ? undefined : 'none' }}>
            {/* Sub-navigation */}
            <div className="view-subnav">
              <div className="subnav-tabs">
                <button
                  className={`subnav-item ${productSubView === 'catalog' ? 'active' : ''}`}
                  onClick={() => setProductSubView('catalog')}
                >
                  Catalog
                </button>
                <button
                  className={`subnav-item ${productSubView === 'categories' ? 'active' : ''}`}
                  onClick={() => setProductSubView('categories')}
                >
                  Categories
                </button>
                <button
                  className={`subnav-item ${productSubView === 'attributes' ? 'active' : ''}`}
                  onClick={() => setProductSubView('attributes')}
                >
                  Attributes
                </button>
                <button
                  className={`subnav-item ${productSubView === 'clients' ? 'active' : ''}`}
                  onClick={() => setProductSubView('clients')}
                >
                  Clients
                </button>
              </div>
              
              {productSubView === 'catalog' && (user?.role === 'admin' || user?.role === 'manager' || user?.role === 'staff') && (
                <div className="subnav-actions">
                  <BulkOperations onImportComplete={fetchProducts} selectedProductIds={selectedProductIds} userRole={user?.role || 'viewer'} />
                  <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
                    <span>➕</span> Add Product
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="view-content">
              {productSubView === 'catalog' && (
                <>
                  {/* Dashboard Tiles */}
                  {!showForm && (
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f3f5', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                      <button onClick={() => setProductSubView('catalog')} style={{ padding: '14px 16px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', cursor: 'pointer', textAlign: 'left' }}>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#0d9488' }}>{totalProducts}</div>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#5a6577', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Catalog</div>
                      </button>
                      <button onClick={() => setProductSubView('categories')} style={{ padding: '14px 16px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', cursor: 'pointer', textAlign: 'left' }}>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#0d9488' }}>{categoryCount}</div>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#5a6577', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Categories</div>
                      </button>
                      <button onClick={() => setProductSubView('attributes')} style={{ padding: '14px 16px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', cursor: 'pointer', textAlign: 'left' }}>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#0d9488' }}>{attributeCount}</div>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#5a6577', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Attributes</div>
                      </button>
                    </div>
                  )}
                  {showForm ? (
                    <ProductForm
                      product={editingProduct}
                      onSave={handleFormSubmit}
                      onCancel={handleFormClose}
                    />
                  ) : viewingProduct ? (
                    <div style={{ padding: '24px', maxWidth: '900px' }}>
                      {/* Back button */}
                      <button 
                        onClick={() => setViewingProduct(null)} 
                        className="btn btn-secondary" 
                        style={{ marginBottom: '20px', fontSize: '13px' }}
                      >
                        ← Back to Products
                      </button>

                      {/* Product Detail Card */}
                      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '32px', alignItems: 'start' }}>
                        {/* Image Section */}
                        <div style={{ background: '#fafbfc', borderRadius: '12px', border: '1px solid #e8ecf0', padding: '20px', textAlign: 'center' }}>
                          {viewingProduct.images && viewingProduct.images.length > 0 ? (
                            <img 
                              src={viewingProduct.images[0]?.image_url}
                              alt={viewingProduct.name}
                              style={{ width: '100%', maxHeight: '280px', objectFit: 'contain', borderRadius: '8px' }}
                              onError={(e) => { (e.target as HTMLImageElement).src = ''; (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '48px', background: '#f1f5f9', borderRadius: '8px' }}>
                              □
                            </div>
                          )}
                          {viewingProduct.images && viewingProduct.images.length > 1 && (
                            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                              {viewingProduct.images.slice(1, 5).map((img: any, i: number) => (
                                <img key={i} src={img.image_url} alt="" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e8ecf0' }} />
                              ))}
                              {viewingProduct.images.length > 5 && (
                                <div style={{ width: '48px', height: '48px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#64748b' }}>
                                  +{viewingProduct.images.length - 5}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Details Section */}
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                            <div>
                              <h2 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: 700, color: '#1e293b' }}>{viewingProduct.name}</h2>
                              <div style={{ fontSize: '13px', color: '#64748b', fontFamily: 'monospace' }}>{viewingProduct.sku}</div>
                            </div>
                            {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'staff') && (
                              <>
                              <button className="btn btn-primary" onClick={handleEditFromView} style={{ fontSize: '13px' }}>
                                Edit Product
                              </button>
                              <button className="btn btn-secondary" onClick={() => handleDuplicateProduct()} style={{ fontSize: '13px' }}>
                                Duplicate
                              </button>
                              </>
                            )}
                          </div>

                          {viewingProduct.description && (
                            <p style={{ color: '#475569', fontSize: '14px', lineHeight: 1.6, marginBottom: '20px' }}>{viewingProduct.description}</p>
                          )}

                          {/* Key Details Grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                            <div style={{ padding: '12px 16px', background: '#f8fafb', borderRadius: '8px', border: '1px solid #f0f3f5' }}>
                              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Price</div>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                                {viewingProduct.price ? `₹${viewingProduct.price.toFixed(2)}` : '—'}
                              </div>
                            </div>
                            <div style={{ padding: '12px 16px', background: '#f8fafb', borderRadius: '8px', border: '1px solid #f0f3f5' }}>
                              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Brand</div>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                                {viewingProduct.brand || '—'}
                              </div>
                            </div>
                            <div style={{ padding: '12px 16px', background: '#f8fafb', borderRadius: '8px', border: '1px solid #f0f3f5' }}>
                              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Status</div>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: viewingProduct.status === 'active' ? '#0d9488' : '#64748b', textTransform: 'capitalize' }}>
                                {viewingProduct.workflow_status || viewingProduct.status}
                              </div>
                            </div>
                            <div style={{ padding: '12px 16px', background: '#f8fafb', borderRadius: '8px', border: '1px solid #f0f3f5' }}>
                              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Available Stock</div>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: (viewingProduct.inventory_available || 0) > 0 ? '#16a34a' : '#dc2626' }}>
                                {viewingProduct.inventory_available ?? 0}
                              </div>
                            </div>
                          </div>

                          {/* Taxonomy */}
                          {(viewingProduct.department?.name || viewingProduct.category?.name) && (
                            <div style={{ marginBottom: '20px' }}>
                              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '6px' }}>Taxonomy</div>
                              <div style={{ fontSize: '13px', color: '#475569' }}>
                                {[viewingProduct.department?.name, viewingProduct.category?.name, viewingProduct.subcategory?.name].filter(Boolean).join(' → ')}
                              </div>
                            </div>
                          )}

                          {/* Attributes */}
                          {viewingProduct.attributes && viewingProduct.attributes.length > 0 && (
                            <div>
                              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px' }}>Attributes</div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                {viewingProduct.attributes.map((attr, i) => (
                                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#f8fafb', borderRadius: '6px', fontSize: '13px' }}>
                                    <span style={{ color: '#64748b' }}>{attr.attribute_name}</span>
                                    <span style={{ fontWeight: 500, color: '#1e293b' }}>{attr.value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Bundle Components (shown for bundle products) */}
                      {viewingProduct.product_type === 'bundle' && (
                        <BundleComponentManager productId={viewingProduct.id} productType={viewingProduct.product_type} />
                      )}

                      {/* Related Variants Section */}
                      {(() => {
                        // Find related variants: same parent_id, or children of this product, or same name products
                        const relatedVariants = products.filter(p => {
                          if (p.id === viewingProduct.id) return false;
                          // If viewing a child, show siblings (same parent_id)
                          if (viewingProduct.parent_id && p.parent_id === viewingProduct.parent_id) return true;
                          // If viewing a child, show the parent
                          if (viewingProduct.parent_id && p.id === viewingProduct.parent_id) return true;
                          // If viewing the parent, show children
                          if (p.parent_id === viewingProduct.id) return true;
                          // If no explicit parent relationship, group by same name + taxonomy
                          if (!viewingProduct.parent_id && !p.parent_id && 
                              p.name?.toLowerCase() === viewingProduct.name?.toLowerCase() &&
                              p.department_id === viewingProduct.department_id &&
                              p.category_id === viewingProduct.category_id) return true;
                          return false;
                        });

                        if (relatedVariants.length === 0) return null;

                        return (
                          <div style={{ marginTop: '24px', padding: '20px', background: '#f8fafb', borderRadius: '12px', border: '1px solid #e8ecf0' }}>
                            <div style={{ fontSize: '13px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px', letterSpacing: '0.5px' }}>
                              Related Variants ({relatedVariants.length})
                            </div>
                            <div style={{ display: 'grid', gap: '8px' }}>
                              {relatedVariants.map(variant => {
                                const colorAttr = variant.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value;
                                const sizeAttr = variant.attributes?.find(a => a.attribute_name.toLowerCase() === 'size')?.value;
                                const decorationAttr = variant.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value;
                                return (
                                  <div 
                                    key={variant.id} 
                                    onClick={() => setViewingProduct(variant)}
                                    style={{ 
                                      display: 'flex', alignItems: 'center', gap: '12px', 
                                      padding: '10px 14px', background: 'white', borderRadius: '8px', 
                                      border: '1px solid #e8ecf0', cursor: 'pointer',
                                      transition: 'border-color 0.15s'
                                    }}
                                    onMouseOver={(e) => (e.currentTarget.style.borderColor = '#0d9488')}
                                    onMouseOut={(e) => (e.currentTarget.style.borderColor = '#e8ecf0')}
                                  >
                                    {variant.images && variant.images.length > 0 ? (
                                      <img src={variant.images[0]?.image_url} alt={variant.name} style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e8ecf0', flexShrink: 0 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                    ) : (
                                      <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#94a3b8', flexShrink: 0 }}>□</div>
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {variant.sku}
                                      </div>
                                      <div style={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {[colorAttr && `Color: ${colorAttr}`, sizeAttr && `Size: ${sizeAttr}`, decorationAttr && `Decoration: ${decorationAttr}`].filter(Boolean).join(' · ') || variant.name}
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                      <div style={{ fontSize: '13px', fontWeight: 600, color: (variant.inventory_available || 0) > 0 ? '#16a34a' : '#dc2626' }}>
                                        {variant.inventory_available ?? 0}
                                      </div>
                                      <div style={{ fontSize: '10px', color: '#94a3b8' }}>stock</div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <ProductList
                      products={products}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onBulkDelete={handleBulkDelete}
                      currentPage={currentPage}
                      pageSize={pageSize}
                      totalProducts={totalProducts}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setPageSize}
                      userRole={user?.role || 'viewer'}
                      onSelectionChange={setSelectedProductIds}
                      onSearch={(term: string) => {
                        setProductSearchTerm(term);
                        setCurrentPage(1);
                      }}
                      onFilterChange={(f: any) => {
                        if (f.channel_id !== channelFilter) {
                          setChannelFilter(f.channel_id);
                        }
                        if (f.product_type !== undefined && f.product_type !== productTypeFilter) {
                          setProductTypeFilter(f.product_type || '');
                          setCurrentPage(1);
                        }
                      }}
                    />
                  )}
                </>
              )}

              <div style={{ display: productSubView === 'categories' ? undefined : 'none' }}><TaxonomyManager /></div>
              <div style={{ display: productSubView === 'attributes' ? undefined : 'none' }}><AttributeSetup /></div>
              <div style={{ display: productSubView === 'clients' ? undefined : 'none' }}><ClientCatalog products={products.map(p => ({ id: p.id, sku: p.sku, name: p.name }))} /></div>
            </div>
          </div>
        )}

        {/* Inventory View */}
        {(activeView === 'inventory' || tabs.some(t => t.view === 'inventory')) && (
          <div className="view-container" style={{ display: activeView === 'inventory' ? undefined : 'none' }}>

            {/* Sub-navigation */}
            <div className="view-subnav">
              <button
                className={`subnav-item ${inventoryView === 'stock' ? 'active' : ''}`}
                onClick={() => setInventoryView('stock')}
              >
                Stock
              </button>
              <button
                className={`subnav-item ${inventoryView === 'receipts' ? 'active' : ''}`}
                onClick={() => setInventoryView('receipts')}
              >
                Receipts
              </button>
              <button
                className={`subnav-item ${inventoryView === 'job-orders' ? 'active' : ''}`}
                onClick={() => setInventoryView('job-orders')}
              >
                Job Orders
              </button>
              <button
                className={`subnav-item ${inventoryView === 'reservations' ? 'active' : ''}`}
                onClick={() => setInventoryView('reservations')}
              >
                Reservations
              </button>
              <button
                className={`subnav-item ${inventoryView === 'adjustments' ? 'active' : ''}`}
                onClick={() => setInventoryView('adjustments')}
              >
                Adjustments
              </button>
              <button
                className={`subnav-item ${inventoryView === 'transfers' ? 'active' : ''}`}
                onClick={() => setInventoryView('transfers')}
              >
                Transfers
              </button>
              <button
                className={`subnav-item ${inventoryView === 'movements' ? 'active' : ''}`}
                onClick={() => setInventoryView('movements')}
              >
                Movements
              </button>
              <button
                className={`subnav-item ${inventoryView === 'warehouses' ? 'active' : ''}`}
                onClick={() => setInventoryView('warehouses')}
              >
                Warehouses
              </button>
            </div>

            {/* Content */}
            <div className="view-content">
              <div style={{ display: inventoryView === 'stock' ? undefined : 'none', height: '100%' }}><InventoryStockList /></div>
              <div style={{ display: inventoryView === 'reservations' ? undefined : 'none', height: '100%' }}><ReservationManager /></div>
              <div style={{ display: inventoryView === 'job-orders' ? undefined : 'none', height: '100%' }}><JobOrderManager userRole={user?.role || 'viewer'} /></div>
              <div style={{ display: inventoryView === 'receipts' ? undefined : 'none', height: '100%' }}><GoodsReceiptManager userRole={user?.role || 'viewer'} /></div>
              <div style={{ display: inventoryView === 'movements' ? undefined : 'none', height: '100%' }}><StockMovementList /></div>
              <div style={{ display: inventoryView === 'adjustments' ? undefined : 'none', height: '100%' }}><StockAdjustmentManager /></div>
              <div style={{ display: inventoryView === 'transfers' ? undefined : 'none', height: '100%' }}><StockTransferManager /></div>
              <div style={{ display: inventoryView === 'warehouses' ? undefined : 'none', height: '100%' }}><WarehouseManager /></div>
            </div>
          </div>
        )}

        {/* Orders View */}
        {(activeView === 'orders' || tabs.some(t => t.view === 'orders')) && (
          <div className="view-container" style={{ display: activeView === 'orders' ? undefined : 'none' }}>
            {/* Dashboard - only show in list view */}
            {!getActiveTab()?.entityType && (
              <div className="view-dashboard">
                <OrderDashboard 
                  onFilterClick={(statuses) => setOrderStatusFilter(statuses.join(','))}
                  activeFilters={orderStatusFilter ? orderStatusFilter.split(',') : []}
                  refreshKey={orderDashboardKey}
                />
              </div>
            )}

            {/* Content */}
            <div className="view-content">
              {!getActiveTab()?.entityType && orderView !== 'form' && orderView !== 'import' && (
                <OrderList 
                  onOrderSelect={handleOrderSelect}
                  statusFilter={orderStatusFilter}
                  onAddOrder={handleOrderFormOpen}
                  onImportOrders={handleOrderImportOpen}
                  userRole={user?.role || 'viewer'}
                />
              )}
              {getActiveTab()?.entityType === 'order-detail' && getActiveTab()?.entityId && (
                <OrderDetail orderId={getActiveTab()!.entityId!} onBack={handleOrderBack} userRole={user?.role || 'viewer'} />
              )}
              {orderView === 'form' && (
                <OrderForm 
                  onSave={() => {
                    handleOrderFormClose();
                  }}
                  onCancel={handleOrderFormClose}
                />
              )}
              {orderView === 'import' && (
                <OrderImport 
                  onImportComplete={() => {
                    handleOrderImportClose();
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Reports View */}
        {(activeView === 'reports' || tabs.some(t => t.view === 'reports')) && (
          <div className="view-container" style={{ display: activeView === 'reports' ? undefined : 'none' }}>
            <div className="view-content">
              <ReportsModule />
            </div>
          </div>
        )}

        {/* Analytics View */}
        {(activeView === 'analytics' || tabs.some(t => t.view === 'analytics')) && (
          <div className="view-container" style={{ display: activeView === 'analytics' ? undefined : 'none' }}>
            <div className="view-content">
              <AnalyticsDashboard />
            </div>
          </div>
        )}

        {/* Logistics View */}
        {(activeView === 'logistics' || tabs.some(t => t.view === 'logistics')) && (
          <div className="view-container" style={{ display: activeView === 'logistics' ? undefined : 'none' }}>
            <div className="view-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Logistics Sub-Navigation */}
              <div style={{ padding: '12px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '4px', flexWrap: 'wrap', background: '#f8f9fa' }}>
                {[
                  { key: 'shipments', label: 'Shipments' },
                  { key: 'ndr', label: 'Failed Deliveries (NDR)' },
                  { key: 'rto', label: 'Returns (RTO)' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setLogisticsSubView(tab.key as 'shipments' | 'ndr' | 'rto')}
                    style={{
                      padding: '6px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer',
                      border: logisticsSubView === tab.key ? 'none' : '1px solid #e2e8f0',
                      background: logisticsSubView === tab.key ? '#0d9488' : 'white',
                      color: logisticsSubView === tab.key ? 'white' : '#475569',
                    }}
                  >{tab.label}</button>
                ))}
              </div>
              <div style={{ flex: 1, overflow: 'auto' }}>
                <div style={{ display: logisticsSubView === 'shipments' ? undefined : 'none' }}>
                  <ErrorBoundary>
                    <LogisticsDashboard 
                      key={logisticsDashboardKey}
                      userRole={user?.role || 'viewer'}
                      onCreateShipment={() => setShowShipmentBookingForm(true)}
                      onShipmentSelect={(awb) => {
                        setSelectedShipmentAwb(awb);
                        setShowShipmentTracking(true);
                      }}
                    />
                  </ErrorBoundary>
                </div>
                {logisticsSubView === 'ndr' && (
                  <ErrorBoundary>
                    <NDRManagement userRole={user?.role || 'viewer'} />
                  </ErrorBoundary>
                )}
                {logisticsSubView === 'rto' && (
                  <ErrorBoundary>
                    <RTOManagement userRole={user?.role || 'viewer'} />
                  </ErrorBoundary>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Purchase Orders View */}
        {(activeView === 'purchaseOrders' || tabs.some(t => t.view === 'purchaseOrders')) && (
          <div className="view-container" style={{ display: activeView === 'purchaseOrders' ? undefined : 'none' }}>
            <div className="view-content">
              <PurchaseOrderManager userRole={user?.role || 'viewer'} />
            </div>
          </div>
        )}

        {/* Audit Logs View */}
        {(activeView === 'audit' || tabs.some(t => t.view === 'audit')) && (
          <div className="view-container" style={{ display: activeView === 'audit' ? undefined : 'none' }}>
            <div className="view-content">
              <AuditLogs />
            </div>
          </div>
        )}

        {/* Settings View — Carriers, Rate Cards, Vendors, Channels, Packages, Admin */}
        {(['settings', 'carriers', 'rateCards', 'vendors', 'channels', 'packages', 'admin'].includes(activeView) || tabs.some(t => ['settings', 'carriers', 'rateCards', 'vendors', 'channels', 'packages', 'admin'].includes(t.view))) && (
          <div className="view-container" style={{ display: ['settings', 'carriers', 'rateCards', 'vendors', 'channels', 'packages', 'admin'].includes(activeView) ? undefined : 'none' }}>
            <div className="view-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Settings Sub-Navigation */}
              <div style={{ padding: '12px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '4px', flexWrap: 'wrap', background: '#f8f9fa' }}>
                {[
                  { key: 'carriers', label: 'Carriers' },
                  { key: 'rateCards', label: 'Rate Cards' },
                  { key: 'vendors', label: 'Vendors' },
                  { key: 'channels', label: 'Channels' },
                  { key: 'packages', label: 'Packages' },
                  { key: 'admin', label: 'System' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveView(tab.key)}
                    style={{
                      padding: '6px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer',
                      border: activeView === tab.key ? 'none' : '1px solid #e2e8f0',
                      background: activeView === tab.key ? '#0d9488' : 'white',
                      color: activeView === tab.key ? 'white' : '#475569',
                    }}
                  >{tab.label}</button>
                ))}
              </div>
              {/* Settings Content */}
              <div style={{ flex: 1, overflow: 'auto' }}>
                {activeView === 'carriers' && <ErrorBoundary><CarrierManager userRole={user?.role || 'viewer'} /></ErrorBoundary>}
                {activeView === 'rateCards' && <ErrorBoundary><RateCardManager userRole={user?.role || 'viewer'} /></ErrorBoundary>}
                {activeView === 'vendors' && <VendorManager />}
                {activeView === 'channels' && <ChannelManager userRole={user?.role || 'viewer'} />}
                {activeView === 'packages' && <PackageManager userRole={user?.role || 'viewer'} />}
                {activeView === 'admin' && <><AdminSettings /><UserManagement /></>}
                {activeView === 'settings' && <ErrorBoundary><CarrierManager userRole={user?.role || 'viewer'} /></ErrorBoundary>}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Shipment Booking Form Modal */}
      {showShipmentBookingForm && (
        <div className="modal-overlay" onClick={() => setShowShipmentBookingForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <ShipmentBookingForm
              onSuccess={(shipment) => {
                setShowShipmentBookingForm(false);
                setLogisticsDashboardKey(prev => prev + 1); // Refresh dashboard
                alert(`Shipment booked successfully!\nAWB: ${shipment.awb}`);
              }}
              onCancel={() => setShowShipmentBookingForm(false)}
            />
          </div>
        </div>
      )}

      {/* Shipment Tracking Modal */}
      {showShipmentTracking && selectedShipmentAwb && (
        <div className="modal-overlay" onClick={() => setShowShipmentTracking(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '2px solid #e9ecef' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#212529' }}>Track Shipment</h2>
              <button 
                onClick={() => setShowShipmentTracking(false)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '1.5rem', 
                  cursor: 'pointer',
                  color: '#6c757d',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <ShipmentTracking 
              awb={selectedShipmentAwb}
              onClose={() => setShowShipmentTracking(false)}
            />
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Product"
        message="Are you sure you want to delete this product? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default App;
